import { useRefreshStore } from "@/zustand/useRefreshTokenStore";
import axios, {
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosHeaders,
} from "axios";

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_URL,
});

const getToken = (): string | null => localStorage.getItem("accessToken");

let isRedirecting = false;
const clearStorage = (): void => {
  if (isRedirecting) return; // Prevent multiple redirects
  isRedirecting = true;
  
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");

  // Use replace to avoid back button issues and prevent reload loops
  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
};

const refreshToken = async (): Promise<{
  access: string;
  refresh: string;
} | null> => {
  const { setIsRefreshing } = useRefreshStore.getState();

  try {
    setIsRefreshing(true);
    const refresh = localStorage.getItem("refreshToken");
    if (!refresh) {
      clearStorage();
      setIsRefreshing(false);
      return null;
    }

    const response = await axios.post(
      `${import.meta.env.VITE_APP_BASE_URL}token/refresh/`,
      { refresh },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    const newAccessToken = response.data.access;
    const newRefreshToken = response.data.refresh;

    localStorage.setItem("accessToken", newAccessToken);
    localStorage.setItem("refreshToken", newRefreshToken);

    return { access: newAccessToken, refresh: newRefreshToken };
  } catch {
    clearStorage();
    return null;
  } finally {
    setIsRefreshing(false);
  }
};

// Request interceptor
axiosInstance.interceptors.request.use(
  (request: CustomAxiosRequestConfig) => {
    const headers = new AxiosHeaders();

    // Preserve existing headers (like X-PIN-Verification-Token)
    if (request.headers) {
      const existingHeaders = request.headers;
      Object.keys(existingHeaders).forEach((key) => {
        const value = existingHeaders[key];
        if (value !== undefined && value !== null) {
          headers.set(key, value as string);
        }
      });
    }

    // headers.set("Content-Type", "application/json");
    const token = getToken();

    if (token && !["/login", "/register"].includes(window.location.pathname)) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    request.headers = headers;
    return request;
  },
  (error: AxiosError) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token as string);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;
    const status = error.response?.status;

    if (status === 401) {
      if (!originalRequest._retry) {
        originalRequest._retry = true;

        if (!isRefreshing) {
          isRefreshing = true;
          try {
            const tokens = await refreshToken();
            if (!tokens?.access) {
              processQueue(new Error("Token refresh failed"));
              clearStorage();
              return Promise.reject(error);
            }

            // Update the default headers for future requests
            axiosInstance.defaults.headers.common[
              "Authorization"
            ] = `Bearer ${tokens.access}`;
            processQueue(null, tokens.access);

            return axiosInstance({
              ...originalRequest,
              headers: {
                ...originalRequest.headers,
                Authorization: `Bearer ${tokens.access}`,
              },
            });
          } catch (refreshError) {
            processQueue(refreshError);
            clearStorage();
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        } else {
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (token: string) => {
                originalRequest.headers["Authorization"] = `Bearer ${token}`;
                resolve(axiosInstance(originalRequest));
              },
              reject: (err: unknown) => reject(err),
            });
          });
        }
      } else {
        clearStorage();
      }
    }

    if (status && status >= 400 && status < 600) {
      console.error("HTTP error:", status, error.message);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
